import { MouseEventHandler, type ReactNode, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import DefaultOptionsMenu, {
  MenuOption,
  type MenuOptionProps,
} from '@/components/DefaultOptionsMenu';
import { OptionsIcon } from '@/icons';
import TableCell, { type TableCellProps } from '../TableCell';
import { useToggle } from '@/hooks/useToggle';
import { classes } from './utils';

export { MenuOption, type MenuOptionProps };

export interface OptionsCellProps extends TableCellProps {
  renderExtraOptions?: (onClose: () => void) => ReactNode;
  onEdit: () => void;
  onDelete: () => void;
}

export default function OptionsCell({
  onEdit,
  onDelete,
  renderExtraOptions,
  className,
  ...props
}: OptionsCellProps) {
  const menuToggleRef = useRef(null);
  const [isMenuOpen, toggleMenu] = useToggle(false);

  const handleOpen: MouseEventHandler<SVGSVGElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleMenu();
  };

  const handleEdit = () => {
    onEdit();
    toggleMenu();
  };

  const handleDelete = () => {
    onDelete();
    toggleMenu();
  };

  return (
    <TableCell className={twMerge(classes.optionsCell, className)} {...props}>
      <OptionsIcon
        ref={menuToggleRef}
        className={classes.optionsIcon}
        onClick={handleOpen}
      />
      <DefaultOptionsMenu
        open={isMenuOpen}
        anchorTo={menuToggleRef.current}
        extraOptions={
          renderExtraOptions
            ? renderExtraOptions(() => toggleMenu(false))
            : null
        }
        onEdit={handleEdit}
        onDelete={handleDelete}
        onClose={() => toggleMenu()}
      />
    </TableCell>
  );
}
